import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Plus, ExternalLink, Trash2, Edit, Globe, Settings, ListChecks, Calendar, Bell, X, ArrowUpDown } from "lucide-react";
import type { ThirdPartyListing, Property, MarketingSettings, CustomListingSite } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { format } from "date-fns";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { PropertyGroupFilter } from "@/components/property-group-filter";
import { getGroupFilter, setGroupFilter } from "@/lib/group-filter-state";
import { usePageTitle } from "@/hooks/use-page-title";

const DEFAULT_PLATFORMS = [
  { id: "zillow", name: "Zillow", color: "bg-blue-600", textColor: "text-white" },
  { id: "roomies", name: "Roomies", color: "bg-orange-500", textColor: "text-white" },
  { id: "spare_room", name: "Spare Room", color: "bg-green-600", textColor: "text-white" },
  { id: "hotpads", name: "HotPads", color: "bg-red-500", textColor: "text-white" },
  { id: "facebook_marketplace", name: "Facebook Marketplace", color: "bg-blue-700", textColor: "text-white" },
  { id: "craigslist", name: "Craigslist", color: "bg-purple-600", textColor: "text-white" },
  { id: "furnish_finders", name: "Furnish Finders", color: "bg-teal-600", textColor: "text-white" },
];

const FILTER_STORAGE_KEY = "padpulse_third_party_listings_filters";

type PersistedFilters = {
  reminderFilter?: boolean;
  reminderWindowDays?: number;
  publishedFrom?: string;
  publishedTo?: string;
  propertyFilter?: string;
  platformFilter?: string;
};

function loadPersistedFilters(): PersistedFilters {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as PersistedFilters;
  } catch {}
  return {};
}

function savePersistedFilters(filters: PersistedFilters): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {}
}

function parseLocalDate(value: string): Date {
  const [y, m, d] = value.split("-").map((n) => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

function getPlatformInfo(id: string, customSites: CustomListingSite[]) {
  const builtIn = DEFAULT_PLATFORMS.find((p) => p.id === id);
  if (builtIn) return builtIn;
  const custom = customSites.find((s) => s.id === id);
  if (custom) return { id: custom.id, name: custom.name, color: custom.color || "bg-gray-500", textColor: "text-white" };
  return { id, name: id, color: "bg-gray-400", textColor: "text-white" };
}

function PlatformInitials({ platformId, customSites }: { platformId: string; customSites: CustomListingSite[] }) {
  const info = getPlatformInfo(platformId, customSites);
  const initials = info.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
  return (
    <div className={`h-10 w-10 rounded-lg ${info.color} ${info.textColor} flex items-center justify-center text-sm font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

type FormState = {
  platform: string;
  url: string;
  propertyId: string;
  status: "active" | "inactive";
  notes: string;
  datePublished: string;
  reminderDate: string;
};

const DEFAULT_FORM: FormState = {
  platform: "",
  url: "",
  propertyId: "",
  status: "active",
  notes: "",
  datePublished: "",
  reminderDate: "",
};

type TabView = "listings" | "settings";

export default function AppMarketingThirdPartyListings() {
  const queryClient = useQueryClient();
  usePageTitle("Third-Party Listings");
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "listings", {
    tabs: ["listings", "settings"],
  });
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [showAddSite, setShowAddSite] = useState(false);
  const [newSiteName, setNewSiteName] = useState("");
  const [editFromUrl, setEditFromUrl] = useState<number | null>(null);

  const [groupFilter, setGroupFilterState] = useState<string>(() => getGroupFilter("third-party-listings"));
  const [propertyFilter, setPropertyFilter] = useState<string>(() => loadPersistedFilters().propertyFilter ?? "all");
  const [platformFilter, setPlatformFilter] = useState<string>(() => loadPersistedFilters().platformFilter ?? "all");
  const [reminderFilter, setReminderFilter] = useState<boolean>(() => loadPersistedFilters().reminderFilter ?? false);
  const [reminderWindowDays, setReminderWindowDays] = useState<number>(() => loadPersistedFilters().reminderWindowDays ?? 7);
  const [publishedFrom, setPublishedFrom] = useState<string>(() => loadPersistedFilters().publishedFrom ?? "");
  const [publishedTo, setPublishedTo] = useState<string>(() => loadPersistedFilters().publishedTo ?? "");
  type SortOption = "reminder-soonest" | "newest-published" | "oldest-published";
  const [sortOption, setSortOption] = useState<SortOption>("newest-published");
  const sortManuallySet = useRef(false);

  useEffect(() => {
    savePersistedFilters({ reminderFilter, reminderWindowDays, publishedFrom, publishedTo, propertyFilter, platformFilter });
  }, [reminderFilter, reminderWindowDays, publishedFrom, publishedTo, propertyFilter, platformFilter]);

  useEffect(() => {
    if (sortManuallySet.current) return;
    setSortOption(reminderFilter ? "reminder-soonest" : "newest-published");
  }, [reminderFilter]);

  function handleSortChange(value: SortOption) {
    sortManuallySet.current = true;
    setSortOption(value);
  }

  const [strategyNotesDraft, setStrategyNotesDraft] = useState<string>("");
  const [notesSaveStatus, setNotesSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const notesInitialized = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const listingsQuery = useQuery<ThirdPartyListing[]>({
    queryKey: ["/api/app/marketing/third-party-listings"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const settingsQuery = useQuery<MarketingSettings | null>({
    queryKey: ["/api/app/marketing/settings"],
  });

  useEffect(() => {
    if (settingsQuery.data && !notesInitialized.current) {
      setStrategyNotesDraft(settingsQuery.data.strategyNotes ?? "");
      notesInitialized.current = true;
    }
  }, [settingsQuery.data]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (editId) {
      setEditFromUrl(parseInt(editId, 10));
    }
    const urlPropertyId = params.get("propertyId");
    if (urlPropertyId) {
      setPropertyFilter(urlPropertyId);
    }
    const urlPlatform = params.get("platform");
    if (urlPlatform) {
      setPlatformFilter(urlPlatform);
    }
    if (params.get("new") === "1") {
      setEditingId(null);
      setForm({ ...DEFAULT_FORM, propertyId: urlPropertyId || "" });
      setShowDialog(true);
      const cleanedParams = new URLSearchParams(window.location.search);
      cleanedParams.delete("new");
      const newSearch = cleanedParams.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }
  }, []);

  useEffect(() => {
    if (editFromUrl && listingsQuery.data) {
      const listing = listingsQuery.data.find(l => l.id === editFromUrl);
      if (listing) {
        openEdit(listing);
        setEditFromUrl(null);
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, [editFromUrl, listingsQuery.data]);

  const settingsMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/app/marketing/settings", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/settings"] });
      toast({ title: "Settings saved" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to save settings",
        description: parseErrorMessage(err, "Couldn't save your settings. Please try again."),
        variant: "destructive",
      });
    },
  });

  const notesMutation = useMutation({
    mutationFn: async (notes: string) =>
      apiRequest("POST", "/api/app/marketing/settings", { strategyNotes: notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/settings"] });
      setNotesSaveStatus("saved");
      setTimeout(() => setNotesSaveStatus("idle"), 2000);
    },
    onError: (err: unknown) => {
      setNotesSaveStatus("idle");
      toast({
        title: "Failed to save notes",
        description: parseErrorMessage(err, "Couldn't save these notes. Please try again."),
        variant: "destructive",
      });
    },
  });

  const handleNotesChange = useCallback((value: string) => {
    setStrategyNotesDraft(value);
    setNotesSaveStatus("saving");
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      notesMutation.mutate(value);
    }, 800);
  }, [notesMutation]);

  const createMutation = useMutation({
    mutationFn: async (
      data: Omit<FormState, "propertyId" | "datePublished" | "reminderDate"> & {
        propertyId: number | null;
        datePublished: string | null;
        reminderDate: string | null;
      },
    ) => apiRequest("POST", "/api/app/marketing/third-party-listings", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/third-party-listings"] });
      setShowDialog(false);
      setForm(DEFAULT_FORM);
      toast({ title: "Listing added" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to add listing",
        description: parseErrorMessage(err, "Couldn't add this listing. Please try again."),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<Omit<FormState, "propertyId" | "datePublished" | "reminderDate">> & {
        propertyId?: number | null;
        datePublished?: string | null;
        reminderDate?: string | null;
      };
    }) => apiRequest("PATCH", `/api/app/marketing/third-party-listings/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/third-party-listings"] });
      setShowDialog(false);
      setEditingId(null);
      setForm(DEFAULT_FORM);
      toast({ title: "Listing updated" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to update listing",
        description: parseErrorMessage(err, "Couldn't update this listing. Please try again."),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/marketing/third-party-listings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/third-party-listings"] });
      toast({ title: "Listing removed" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to remove listing",
        description: parseErrorMessage(err, "Couldn't remove this listing. Please try again."),
        variant: "destructive",
      });
    },
  });

  const listings = listingsQuery.data ?? [];
  const properties = sortProperties(propertiesQuery.data ?? []);
  const settings = settingsQuery.data;
  const enabledPlatforms: string[] = Array.isArray(settings?.enabledPlatforms) ? (settings.enabledPlatforms as string[]) : DEFAULT_PLATFORMS.map(p => p.id);
  const customSites: CustomListingSite[] = Array.isArray(settings?.customListingSites) ? (settings.customListingSites as CustomListingSite[]) : [];

  const allPlatforms = [
    ...DEFAULT_PLATFORMS,
    ...customSites.map(s => ({ id: s.id, name: s.name, color: s.color || "bg-gray-500", textColor: "text-white" })),
  ];

  const activePlatforms = allPlatforms.filter(p => enabledPlatforms.includes(p.id));

  function handleGroupFilterChange(value: string) {
    setGroupFilterState(value);
    setGroupFilter("third-party-listings", value);
    setPropertyFilter("all");
  }

  const groupFilteredProperties = useMemo(() => {
    if (groupFilter === "all") return properties;
    return properties.filter(p => (p.propertyGroup || "") === groupFilter);
  }, [properties, groupFilter]);

  const groupFilteredPropertyIds = useMemo(
    () => new Set(groupFilteredProperties.map(p => p.id)),
    [groupFilteredProperties]
  );

  const filteredListings = useMemo(() => {
    let result = listings;
    if (propertyFilter !== "all") {
      result = result.filter(l => l.propertyId !== null && String(l.propertyId) === propertyFilter);
    } else if (groupFilter !== "all") {
      result = result.filter(l => l.propertyId !== null && groupFilteredPropertyIds.has(l.propertyId as number));
    }
    if (reminderFilter) {
      const windowEnd = new Date();
      windowEnd.setDate(windowEnd.getDate() + reminderWindowDays);
      windowEnd.setHours(23, 59, 59, 999);
      result = result.filter(l => {
        if (!l.reminderDate) return false;
        const d = new Date(l.reminderDate);
        return d <= windowEnd;
      });
    }
    if (publishedFrom) {
      const from = parseLocalDate(publishedFrom);
      result = result.filter(l => {
        if (!l.datePublished) return false;
        return new Date(l.datePublished) >= from;
      });
    }
    if (publishedTo) {
      const to = parseLocalDate(publishedTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(l => {
        if (!l.datePublished) return false;
        return new Date(l.datePublished) <= to;
      });
    }
    return result;
  }, [listings, propertyFilter, groupFilter, groupFilteredPropertyIds, reminderFilter, reminderWindowDays, publishedFrom, publishedTo]);

  const reminderDueCount = useMemo(() => {
    const windowEnd = new Date();
    windowEnd.setDate(windowEnd.getDate() + reminderWindowDays);
    windowEnd.setHours(23, 59, 59, 999);
    return listings.filter(l => {
      if (!l.reminderDate) return false;
      return new Date(l.reminderDate) <= windowEnd;
    }).length;
  }, [listings, reminderWindowDays]);

  function sortListings(items: ThirdPartyListing[]): ThirdPartyListing[] {
    const arr = [...items];
    arr.sort((a, b) => {
      if (sortOption === "reminder-soonest") {
        const av = a.reminderDate ? new Date(a.reminderDate).getTime() : Infinity;
        const bv = b.reminderDate ? new Date(b.reminderDate).getTime() : Infinity;
        return av - bv;
      }
      const av = a.datePublished ? new Date(a.datePublished).getTime() : null;
      const bv = b.datePublished ? new Date(b.datePublished).getTime() : null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return sortOption === "newest-published" ? bv - av : av - bv;
    });
    return arr;
  }

  const listingsByPlatform = activePlatforms
    .filter((platform) => platformFilter === "all" || platform.id === platformFilter)
    .map((platform) => ({
      platform,
      listings: sortListings(filteredListings.filter((l) => l.platform === platform.id)),
    }));

  function togglePlatform(platformId: string, enabled: boolean) {
    let updated: string[];
    if (enabled) {
      updated = [...enabledPlatforms, platformId];
    } else {
      updated = enabledPlatforms.filter(id => id !== platformId);
    }
    settingsMutation.mutate({ enabledPlatforms: updated });
  }

  function handleAddCustomSite() {
    if (!newSiteName.trim()) return;
    const id = newSiteName.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (allPlatforms.some(p => p.id === id)) {
      toast({ title: "A platform with that name already exists", variant: "destructive" });
      return;
    }
    const newSite: CustomListingSite = { id, name: newSiteName.trim(), color: "bg-gray-500" };
    const updatedSites = [...customSites, newSite];
    const updatedEnabled = [...enabledPlatforms, id];
    settingsMutation.mutate({ customListingSites: updatedSites, enabledPlatforms: updatedEnabled });
    setNewSiteName("");
    setShowAddSite(false);
  }

  function handleRemoveCustomSite(siteId: string) {
    const updatedSites = customSites.filter(s => s.id !== siteId);
    const updatedEnabled = enabledPlatforms.filter(id => id !== siteId);
    settingsMutation.mutate({ customListingSites: updatedSites, enabledPlatforms: updatedEnabled });
  }

  function openAdd() {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setShowDialog(true);
  }

  function openEdit(listing: ThirdPartyListing) {
    setEditingId(listing.id);
    setForm({
      platform: listing.platform,
      url: listing.url,
      propertyId: listing.propertyId ? String(listing.propertyId) : "",
      status: listing.status as "active" | "inactive",
      notes: listing.notes || "",
      datePublished: listing.datePublished ? format(new Date(listing.datePublished), "yyyy-MM-dd") : "",
      reminderDate: listing.reminderDate ? format(new Date(listing.reminderDate), "yyyy-MM-dd") : "",
    });
    setShowDialog(true);
  }

  function handleSubmit() {
    const propertyId = form.propertyId ? parseInt(form.propertyId, 10) : null;
    const payload = {
      ...form,
      propertyId,
      datePublished: form.datePublished || null,
      reminderDate: form.reminderDate || null,
    };
    if (editingId !== null) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  const isFiltering = groupFilter !== "all" || propertyFilter !== "all" || platformFilter !== "all" || reminderFilter || !!publishedFrom || !!publishedTo;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-tour="third-party-listings-heading">Third Party Listings</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Track your listings on external platforms. All listings ultimately route prospects to your PadSplit referral link.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "listings" && (
              <Button onClick={openAdd} data-testid="button-add-listing" data-tour="third-party-listings-add">
                <Plus className="h-4 w-4 mr-1.5" />
                Add Listing
              </Button>
            )}
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="third-party-listings" />
              <GuideButton pageKey="third-party-listings" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="third-party-listings" />
        </div>

        <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
          <button
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "listings"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("listings")}
            data-testid="tab-listings"
          >
            <ListChecks className="h-4 w-4 inline mr-1.5" />
            Listings
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "settings"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("settings")}
            data-testid="tab-settings"
          >
            <Settings className="h-4 w-4 inline mr-1.5" />
            Settings
          </button>
        </div>

        {activeTab === "settings" ? (
          <div className="space-y-6">
            <Card data-testid="card-reminder-settings">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Default Reminder</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Automatically set a reminder when new listings are created. The reminder date is calculated as the publish date plus the number of days below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Label className="text-sm whitespace-nowrap">Remind me after</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={settings?.defaultReminderDays ?? ""}
                    onChange={(e) => {
                      const val = e.target.value ? parseInt(e.target.value, 10) : null;
                      settingsMutation.mutate({ defaultReminderDays: val });
                    }}
                    className="w-20"
                    placeholder="—"
                    data-testid="input-default-reminder-days"
                  />
                  <span className="text-sm text-gray-600">days</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {settings?.defaultReminderDays
                    ? `New listings will automatically get a reminder ${settings.defaultReminderDays} day${settings.defaultReminderDays !== 1 ? "s" : ""} after the publish date.`
                    : "No default set — reminders must be added manually per listing."}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-platform-settings">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Listing Platforms</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Toggle platforms on or off. Enabled platforms appear in the listings view and can have listings tracked against them.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {DEFAULT_PLATFORMS.map((platform) => {
                    const isEnabled = enabledPlatforms.includes(platform.id);
                    const listingCount = listings.filter(l => l.platform === platform.id).length;
                    return (
                      <div
                        key={platform.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                        data-testid={`platform-setting-${platform.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <PlatformInitials platformId={platform.id} customSites={customSites} />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{platform.name}</p>
                            {listingCount > 0 && (
                              <p className="text-xs text-gray-500">{listingCount} listing{listingCount !== 1 ? "s" : ""}</p>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => togglePlatform(platform.id, checked)}
                          data-testid={`switch-platform-${platform.id}`}
                        />
                      </div>
                    );
                  })}

                  {customSites.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Custom Platforms</p>
                      {customSites.map((site) => {
                        const isEnabled = enabledPlatforms.includes(site.id);
                        const listingCount = listings.filter(l => l.platform === site.id).length;
                        return (
                          <div
                            key={site.id}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg mb-2"
                            data-testid={`platform-setting-${site.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <PlatformInitials platformId={site.id} customSites={customSites} />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{site.name}</p>
                                {listingCount > 0 && (
                                  <p className="text-xs text-gray-500">{listingCount} listing{listingCount !== 1 ? "s" : ""}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => togglePlatform(site.id, checked)}
                                data-testid={`switch-platform-${site.id}`}
                              />
                              <button
                                onClick={() => handleRemoveCustomSite(site.id)}
                                className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"
                                data-testid={`button-remove-site-${site.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  {showAddSite ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={newSiteName}
                        onChange={(e) => setNewSiteName(e.target.value)}
                        placeholder="Platform name"
                        className="flex-1"
                        data-testid="input-new-site-name"
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddCustomSite(); }}
                      />
                      <Button size="sm" onClick={handleAddCustomSite} disabled={!newSiteName.trim() || settingsMutation.isPending} data-testid="button-save-custom-site">
                        Add
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowAddSite(false); setNewSiteName(""); }} data-testid="button-cancel-custom-site">
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddSite(true)}
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      data-testid="button-add-custom-platform"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Custom Platform
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            <Card className="mb-5" data-testid="card-strategy-notes">
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-700">Listing Strategy Notes</CardTitle>
                  {notesSaveStatus === "saving" && (
                    <span className="text-xs text-gray-400" data-testid="text-notes-saving">Saving…</span>
                  )}
                  {notesSaveStatus === "saved" && (
                    <span className="text-xs text-green-600" data-testid="text-notes-saved">Saved</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                {settingsQuery.isLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : (
                  <Textarea
                    value={strategyNotesDraft}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Capture general notes about your listing strategy here — e.g. which platforms perform best, pricing guidance, seasonal patterns..."
                    rows={3}
                    className="resize-y text-sm"
                    data-testid="textarea-strategy-notes"
                  />
                )}
              </CardContent>
            </Card>

            <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="filter-row-listings" data-tour="third-party-listings-filters">
              <PropertyGroupFilter
                properties={properties}
                value={groupFilter}
                onChange={handleGroupFilterChange}
              />
              <SearchableSelect
                value={propertyFilter}
                onValueChange={setPropertyFilter}
                placeholder="All Properties"
                searchPlaceholder="Search properties..."
                emptyText="No properties match."
                testId="select-listings-property-filter"
                className="h-9 w-[200px] text-sm"
                options={[
                  { value: "all", label: "All Properties" },
                  ...groupFilteredProperties.map((p) => ({
                    value: String(p.id),
                    label: p.name || p.address || `Property #${p.id}`,
                    keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                  })),
                ]}
              />
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="select-listings-platform-filter">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {activePlatforms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setReminderFilter(!reminderFilter)}
                className={`h-9 px-3 text-xs font-medium rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                  reminderFilter
                    ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
                data-testid="chip-reminder-due"
              >
                <Bell className="h-3.5 w-3.5" />
                Due for reminder
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${reminderFilter ? "bg-amber-200 text-amber-800" : "bg-gray-100 text-gray-500"}`} data-testid="text-reminder-due-count">
                  {reminderDueCount}
                </span>
              </button>
              {reminderFilter && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span>within</span>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={reminderWindowDays}
                    onChange={(e) => setReminderWindowDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="h-7 w-16 text-xs"
                    data-testid="input-reminder-window-days"
                  />
                  <span>days</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                <span>Published</span>
                <Input
                  type="date"
                  value={publishedFrom}
                  onChange={(e) => setPublishedFrom(e.target.value)}
                  className="h-9 w-[150px] text-xs"
                  data-testid="input-published-from"
                />
                <span>to</span>
                <Input
                  type="date"
                  value={publishedTo}
                  onChange={(e) => setPublishedTo(e.target.value)}
                  className="h-9 w-[150px] text-xs"
                  data-testid="input-published-to"
                />
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
                <span>Sort</span>
                <Select value={sortOption} onValueChange={(v) => handleSortChange(v as SortOption)}>
                  <SelectTrigger className="h-9 w-[200px] text-xs" data-testid="select-listings-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reminder-soonest">Reminder soonest</SelectItem>
                    <SelectItem value="newest-published">Newest published</SelectItem>
                    <SelectItem value="oldest-published">Oldest published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isFiltering && (
                <button
                  onClick={() => {
                    handleGroupFilterChange("all");
                    setPropertyFilter("all");
                    setPlatformFilter("all");
                    setReminderFilter(false);
                    setReminderWindowDays(7);
                    setPublishedFrom("");
                    setPublishedTo("");
                    sortManuallySet.current = false;
                    setSortOption("newest-published");
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                  data-testid="button-clear-listing-filters"
                >
                  Clear filters
                </button>
              )}
            </div>

            {listingsQuery.isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-36 w-full" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="third-party-listings-table">
                {listingsByPlatform.map(({ platform, listings: platListings }) => (
                  <Card key={platform.id} className="overflow-hidden" data-testid={`card-platform-${platform.id}`}>
                    <CardHeader className="pb-2 pt-4 px-4">
                      <div className="flex items-center gap-3">
                        <PlatformInitials platformId={platform.id} customSites={customSites} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{platform.name}</p>
                          <p className="text-xs text-gray-500">
                            {platListings.filter(l => l.status === "active").length} active listing{platListings.filter(l => l.status === "active").length !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      {platListings.length === 0 ? (
                        <div className="border border-dashed border-gray-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-400">
                            {isFiltering ? "No listings match your filters" : "No listings tracked"}
                          </p>
                          {!isFiltering && (
                            <button
                              onClick={() => {
                                setForm({ ...DEFAULT_FORM, platform: platform.id });
                                setEditingId(null);
                                setShowDialog(true);
                              }}
                              className="text-xs text-blue-600 hover:underline mt-1"
                              data-testid={`button-add-listing-${platform.id}`}
                            >
                              + Add listing
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {platListings.map((listing) => {
                            const property = properties.find((p) => p.id === listing.propertyId);
                            return (
                              <div key={listing.id} className="border border-gray-100 rounded-lg p-2.5 group" data-testid={`listing-item-${listing.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    {property && (
                                      <p className="text-xs font-medium text-gray-700 truncate">{property.name}</p>
                                    )}
                                    <a
                                      href={listing.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline flex items-center gap-0.5 truncate"
                                      data-testid={`listing-url-${listing.id}`}
                                    >
                                      <Globe className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{listing.url}</span>
                                      <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                                    </a>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => openEdit(listing)}
                                      className="p-1 hover:bg-gray-100 rounded"
                                      data-testid={`button-edit-listing-${listing.id}`}
                                    >
                                      <Edit className="h-3 w-3 text-gray-500" />
                                    </button>
                                    <button
                                      onClick={() => deleteMutation.mutate(listing.id)}
                                      className="p-1 hover:bg-red-50 rounded"
                                      data-testid={`button-delete-listing-${listing.id}`}
                                    >
                                      <Trash2 className="h-3 w-3 text-red-400" />
                                    </button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                  <Badge
                                    variant="outline"
                                    className={`text-xs px-1.5 py-0 ${
                                      listing.status === "active"
                                        ? "text-green-700 border-green-200 bg-green-50"
                                        : "text-gray-500 border-gray-200 bg-gray-50"
                                    }`}
                                  >
                                    {listing.status}
                                  </Badge>
                                  {listing.datePublished && (
                                    <span className="text-xs text-gray-400 flex items-center gap-0.5">
                                      <Calendar className="h-3 w-3" />
                                      {format(new Date(listing.datePublished), "MMM d, yyyy")}
                                    </span>
                                  )}
                                  {listing.reminderDate && (
                                    <span className="text-xs text-amber-600 flex items-center gap-0.5">
                                      <Bell className="h-3 w-3" />
                                      {format(new Date(listing.reminderDate), "MMM d")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {!isFiltering && (
                            <button
                              onClick={() => {
                                setForm({ ...DEFAULT_FORM, platform: platform.id });
                                setEditingId(null);
                                setShowDialog(true);
                              }}
                              className="text-xs text-blue-600 hover:underline w-full text-left mt-1"
                              data-testid={`button-add-another-${platform.id}`}
                            >
                              + Add another
                            </button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setShowDialog(false); setEditingId(null); setForm(DEFAULT_FORM); } }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="dialog-listing">
            <DialogHeader>
              <DialogTitle>{editingId !== null ? "Edit Listing" : "Add Listing"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm">Platform</Label>
                <Select
                  value={form.platform}
                  onValueChange={(v) => setForm({ ...form, platform: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="select-platform">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {activePlatforms.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Listing URL</Label>
                <Input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://www.zillow.com/..."
                  className="mt-1"
                  data-testid="input-listing-url"
                />
              </div>
              <div>
                <Label className="text-sm">Property (optional)</Label>
                <SearchableSelect
                  value={form.propertyId || "none"}
                  onValueChange={(v) => setForm({ ...form, propertyId: v === "none" ? "" : v })}
                  placeholder="Select property"
                  searchPlaceholder="Search properties..."
                  emptyText="No properties match."
                  testId="select-property"
                  className="mt-1"
                  modal={true}
                  options={[
                    { value: "none", label: "— No property —" },
                    ...properties.map((p) => ({
                      value: String(p.id),
                      label: p.name,
                      keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                    })),
                  ]}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Date Published
                  </Label>
                  <Input
                    type="date"
                    value={form.datePublished}
                    onChange={(e) => setForm({ ...form, datePublished: e.target.value })}
                    className="mt-1"
                    data-testid="input-date-published"
                  />
                </div>
                <div>
                  <Label className="text-sm flex items-center gap-1.5">
                    <Bell className="h-3.5 w-3.5" />
                    Reminder Date
                  </Label>
                  <Input
                    type="date"
                    value={form.reminderDate}
                    onChange={(e) => setForm({ ...form, reminderDate: e.target.value })}
                    className="mt-1"
                    data-testid="input-reminder-date"
                  />
                  <p className="text-xs text-gray-400 mt-1">Shows as a reminder on your dashboard.</p>
                </div>
              </div>
              <div>
                <Label className="text-sm">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v as "active" | "inactive" })}
                >
                  <SelectTrigger className="mt-1" data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Notes (optional)</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any notes about this listing..."
                  className="mt-1"
                  rows={2}
                  data-testid="input-listing-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); setEditingId(null); setForm(DEFAULT_FORM); }} data-testid="button-cancel-dialog">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isMutating || !form.platform || !form.url}
                data-testid="button-save-listing"
              >
                {isMutating ? "Saving..." : editingId !== null ? "Save Changes" : "Add Listing"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
